import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROJECT } from "@/data";
import { useLang } from "@/i18n";

function WorkImage({ src, alt, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`group relative overflow-hidden border border-[#27272A] ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-[800ms] ease-out"
      />
      <div className="absolute inset-0 bg-brand/0 group-hover:bg-brand/10 transition-colors duration-500 mix-blend-multiply" />
    </div>
  );
}

export default function Projects() {
  const { t } = useLang();
  return (
    <section
      id="work"
      data-testid="projects-section"
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-b border-[#27272A]"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
        <div>
          <p className="font-satoshi font-bold text-xs tracking-[0.3em] uppercase text-accent mb-5 flex items-center gap-2">
            <span className="w-4 h-4 bg-brand" /> {t.work.label}
          </p>
          <h2 className="font-cabinet font-black uppercase text-6xl md:text-8xl tracking-tighter leading-[0.8]">
            {t.work.h1}
            <br />
            <span className="text-outline-accent">{t.work.h2}</span>
          </h2>
        </div>
        <p className="font-satoshi text-lg text-[#a1a1aa] max-w-sm md:text-right">
          {t.work.intro}
        </p>
      </div>

      <motion.article
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`project-${PROJECT.id}`}
        className="border border-[#27272A]"
      >
        <div className="flex flex-wrap items-end justify-between gap-4 p-6 md:p-8 border-b border-[#27272A] bg-[#0c0c0e]">
          <div>
            <p className="font-satoshi font-bold text-xs uppercase tracking-widest text-accent mb-2">
              {PROJECT.brand} · {t.work.tag}
            </p>
            <h3 className="font-cabinet font-black uppercase text-4xl md:text-6xl tracking-tighter leading-none">
              {PROJECT.title}
            </h3>
          </div>
          <span className="font-satoshi font-bold text-sm text-[#71717a]">{PROJECT.year}</span>
        </div>

        <WorkImage
          src={PROJECT.cover}
          alt={PROJECT.title}
          testid="project-cover"
          className="aspect-[16/10] md:aspect-[16/7] border-0 border-b border-[#27272A]"
        />

        <div className="grid md:grid-cols-12">
          <div className="md:col-span-4 flex flex-col justify-between gap-8 p-6 md:p-8 border-b md:border-b-0 md:border-r border-[#27272A]">
            <p className="font-satoshi text-lg md:text-xl text-[#a1a1aa] leading-relaxed">
              {t.work.description}
            </p>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="project-cta"
              className="group inline-flex items-center gap-3 font-satoshi font-bold text-sm uppercase tracking-widest bg-brand text-white px-5 py-3 self-start hover:bg-white hover:text-[#09090B] transition-colors duration-200"
            >
              {t.work.cta}
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </div>
          <WorkImage
            src={PROJECT.gallery[0]}
            alt={`${PROJECT.title} detail 1`}
            testid="project-gallery-0"
            className="md:col-span-4 aspect-[4/5] md:aspect-auto border-0 border-b md:border-b-0 md:border-r border-[#27272A]"
          />
          <WorkImage
            src={PROJECT.gallery[1]}
            alt={`${PROJECT.title} detail 2`}
            testid="project-gallery-1"
            className="md:col-span-4 aspect-[4/5] md:aspect-auto border-0"
          />
        </div>
      </motion.article>
    </section>
  );
}
